import {
  LayoutGrid,
  Sparkles,
  ListChecks,
  Layers,
  PenSquare,
  Columns3,
  Network,
  GraduationCap,
  HelpCircle,
  BookMarked,
  Scale,
  ArrowRight,
} from"lucide-react";

// Cartes de synthèse présentant les modules du site.
// Chaque carte est cliquable et navigue vers la vue correspondante.
const FEATURES = [
  {
    id:"carte",
    icon: LayoutGrid,
    title:"Carte interactive du PCG",
    desc:"Toute l'arborescence des classes 1 à 8 sur une grille cliquable. Repérez visuellement chaque compte à 2 chiffres.",
    color:"#15294F",
    accent:"#EBF1FB",
  },
  {
    id:"reforme",
    icon: Sparkles,
    title:"Points clés de la réforme",
    desc:"Comptes supprimés, créés, renommés : maîtrisez les changements applicables aux exercices ouverts à partir du 1ᵉʳ janvier 2025.",
    color:"#A33B4B",
    accent:"#FBF1F1",
  },
  {
    id:"methodologie",
    icon: ListChecks,
    title:"Logique de saisie",
    desc:"La méthode pas-à-pas pour passer n'importe quelle écriture : nature de l'opération, sens, équilibre débit/crédit.",
    color:"#2D6BE0",
    accent:"#EFF6FF",
  },
  {
    id:"logique",
    icon: Layers,
    title:"Logique des classes",
    desc:"Pourquoi 6 = charges, 28 = amortissements, le rôle du chiffre 8. Comprenez la grammaire numérique du PCG.",
    color:"#3B1E6E",
    accent:"#F2EFF8",
  },
  {
    id:"ecritures",
    icon: PenSquare,
    title:"Écritures fréquentes",
    desc:"Catalogue prêt à l'emploi : ventes, achats, paie, immobilisations, TVA, OD de clôture. Chaque écriture détaillée.",
    color:"#1F4D3A",
    accent:"#F0F5F0",
  },
  {
    id:"comparateur",
    icon: Columns3,
    title:"Comparateur de comptes",
    desc:"Affichez jusqu'à 4 comptes côte à côte pour lever les confusions classiques (401/404, 411/416, 281/291…).",
    color:"#2F7A5C",
    accent:"#ECFDF5",
  },
  {
    id:"flux",
    icon: Network,
    title:"Liens comptables entre états financiers",
    desc:"Voyez circuler l'information du journal vers le grand-livre, la balance, le bilan et le compte de résultat.",
    color:"#B85724",
    accent:"#FEF3EC",
  },
  {
    id:"quiz",
    icon: GraduationCap,
    title:"Auto-évaluation",
    desc:"Testez votre maîtrise des comptes et de la logique du PCG avec des QCM corrigés et chronométrés.",
    color:"#7C3AED",
    accent:"#F3EFFF",
  },
  {
    id:"faq",
    icon: HelpCircle,
    title:"Questions fréquentes",
    desc:"Les réponses pédagogiques aux questions que tout étudiant et entrepreneur se pose : TVA, marges, clôture…",
    color:"#0E7490",
    accent:"#ECFEFF",
  },
  {
    id:"glossaire",
    icon: BookMarked,
    title:"Glossaire",
    desc:"Définitions claires de chaque notion comptable, avec développement, piège classique et mini-cas pratique.",
    color:"#9333EA",
    accent:"#FAF5FF",
  },
];

export default function Welcome({ onChangeView }) {
  return (
    <div data-testid="welcome-view" className="space-y-10">
      {/* Hero */}
      <div className="bg-gradient-to-br from-white to-slate-50 border border-keyto rounded-2xl shadow-keyto p-8 md:p-12 relative overflow-hidden">
        <div className="relative z-10">
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-extrabold tracking-tight text-[#15294F] leading-tight">
            Apprenez le PCG <span className="text-[#2D6BE0]">compte par compte</span>, pas ligne par ligne.
          </h1>
          <div className="mt-6 space-y-4 text-[15px] text-slate-700 leading-relaxed">
            <p>
              Ce site, <strong className="text-[#15294F]">non officiel</strong>, a été
              conçu pour proposer une approche <strong>moderne, pédagogique et
              opérationnelle</strong> de l&apos;apprentissage de la comptabilité. Son
              ambition est de replacer le PCG au cœur de l&apos;apprentissage, en
              l&apos;abordant comme le véritable pivot des écritures comptables et de
              la logique comptable.
            </p>
            <p>
              Destiné principalement aux <strong>étudiants, alternants, collaborateurs
              débutants et profils juniors</strong>, il peut également constituer un
              support de révision ou de référence pour des professionnels plus
              expérimentés.
            </p>
            <p>
              Contrairement aux approches traditionnelles souvent centrées sur les
              écritures « ligne par ligne », cette base documentaire vise à permettre
              une <span className="text-[#2D6BE0] font-semibold">compréhension du PCG compte par compte</span>,
              afin d&apos;en maîtriser progressivement la logique, les mécanismes et les
              cas d&apos;utilisation.
            </p>
            <p className="text-slate-600 italic border-l-2 border-[#2D6BE0] pl-4">
              Cette ressource est <strong className="not-italic text-[#15294F]">collaborative dans son esprit</strong> :
              malgré le soin apporté à son contenu, des imprécisions ou omissions
              peuvent subsister. Si vous identifiez une anomalie, une amélioration ou
              un complément utile, n&apos;hésitez pas à{" "}
              <a
                href="mailto:k.tordjman@keyto-expertise.com"
                className="not-italic font-semibold text-[#2D6BE0] underline underline-offset-2 hover:text-[#15294F]"
              >
                me contacter
              </a>{" "}
              afin de contribuer à l&apos;enrichissement continu de cette base de
              connaissances.
            </p>
          </div>
        </div>
      </div>

      {/* Encadré institutionnel : droit comptable */}
      <aside
        data-testid="welcome-droit-comptable"
        className="relative bg-gradient-to-br from-[#15294F] to-[#1F3B6B] rounded-2xl p-6 md:p-8 text-white overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-1.5 h-full bg-[#5AA0FF]" />
        <div className="flex items-start gap-4">
          <div className="shrink-0 w-12 h-12 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center">
            <Scale className="text-[#5AA0FF]" size={22} />
          </div>
          <div className="flex-1">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#5AA0FF] mb-1.5">
              Rappel essentiel
            </p>
            <h2 className="font-heading text-lg md:text-xl font-bold leading-tight mb-2">
              La comptabilité relève avant tout du <span className="text-[#5AA0FF]">droit comptable</span>.
            </h2>
            <p className="text-sm text-white/85 leading-relaxed">
              Elle est régie par le <strong>Code de commerce</strong> et le <strong>Plan Comptable Général</strong> :
              normes obligatoires, principes (continuité d&apos;exploitation, prudence, permanence des méthodes,
              séparation des exercices, image fidèle), et règles d&apos;enregistrement opposables aux tiers et
              aux administrations.
            </p>
            <p className="text-sm text-white/85 leading-relaxed mt-2">
              Ce n&apos;est qu&apos;<strong>ensuite</strong> qu&apos;elle devient un outil de <strong>pilotage et
              de gestion</strong> : analyse de marges, tableaux de bord, prévisionnels, contrôle de gestion.
              La rigueur juridique précède toujours la lecture managériale.
            </p>
          </div>
        </div>
      </aside>

      {/* Grille de cartes : modules du site */}
      <section data-testid="welcome-features" className="space-y-5">
        <div>
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-[#15294F] tracking-tight">
            Que propose le site ?
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Cliquez sur une carte pour ouvrir le module correspondant.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <button
                key={f.id}
                data-testid={`welcome-feature-${f.id}`}
                onClick={() => onChangeView(f.id)}
                className="group relative text-left bg-white border border-keyto rounded-xl p-5 hover:shadow-keyto hover:-translate-y-0.5 transition-all overflow-hidden"
              >
                <div
                  className="absolute top-0 left-0 h-1 w-full"
                  style={{ backgroundColor: f.color }}
                />
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                  style={{ backgroundColor: f.accent }}
                >
                  <Icon size={20} style={{ color: f.color }} />
                </div>
                <h3
                  className="font-heading font-extrabold text-base leading-tight mb-1.5"
                  style={{ color: f.color }}
                >
                  {f.title}
                </h3>
                <p className="text-[12.5px] text-slate-600 leading-relaxed">{f.desc}</p>
                <div
                  className="mt-3 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all"
                  style={{ color: f.color }}
                >
                  Ouvrir <ArrowRight size={12} />
                </div>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
